import React, { useEffect, useMemo, useState } from "react";
import { useCookies } from "react-cookie";
import { decodeToken } from "react-jwt";
import { useNavigate } from "react-router-dom";

import Loader from "../../../components/Loader";
import { useUserAccount } from "../../../services/create-resume/queries";

function MCPAuth() {
	const [cookies] = useCookies(["Authorization"]);
	const navigate = useNavigate();
	const [hasCheckedAuth, setHasCheckedAuth] = useState(false);

	const authToken = cookies.Authorization;

	// Check if token is expired locally before making API call
	// This prevents the axios interceptor from redirecting without the MCP context
	const isTokenExpired = useMemo(() => {
		if (!authToken) return true;
		if (authToken.includes("LI::")) return false; // LinkedIn tokens handled differently

		const token = authToken.replaceAll("GO::", "").replaceAll("CO::", "");
		try {
			const decoded = decodeToken(token);
			return decoded?.exp < Date.now() / 1000;
		} catch {
			return true;
		}
	}, [authToken]);

	// Only validate with API if token exists and is not expired locally
	const { data: accountData, isLoading, error } = useUserAccount();

	useEffect(() => {
		const params = new URLSearchParams(window.location.search);
		const redirectUrl = params.get("redirect");
		// Build the full return URL including the original redirect parameter
		const returnUrl = redirectUrl ? `/mcp-auth?redirect=${encodeURIComponent(redirectUrl)}` : "/mcp-auth";

		// If no token or token is expired locally, redirect immediately
		// This prevents the axios interceptor from hijacking the redirect
		if (!authToken || isTokenExpired) {
			window.location.href = `/signin?ref=${encodeURIComponent(returnUrl)}`;
			return;
		}

		// Wait for API validation to complete
		if (isLoading) return;

		// If API returned error, token is invalid server-side
		if (error) {
			window.location.href = `/signin?ref=${encodeURIComponent(returnUrl)}`;
			return;
		}

		// If we have valid account data, proceed with redirect
		if (accountData && !hasCheckedAuth) {
			setHasCheckedAuth(true);

			// Redirect to the callback URL with the token
			if (redirectUrl) {
				const callbackUrl = `${redirectUrl}?token=${encodeURIComponent(authToken)}`;
				window.location.href = callbackUrl;
			} else {
				// Fallback if no redirect URL is provided
				navigate("/profile");
			}
		}
	}, [authToken, isTokenExpired, accountData, isLoading, error, navigate, hasCheckedAuth]);

	return (
		<div className="flex items-center justify-center min-h-screen">
			<Loader />
		</div>
	);
}

export default MCPAuth;
